import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Play, Pause, Moon, Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fetchSongs, resolveImage } from "@/lib/api";

const ALL_CATS = ["Praise", "Bible Stories", "Bedtime", "Gratitude", "Movement"];
const AGE_CATS = {
  "0-2": ["Bedtime", "Gratitude"],
  "2-4": ["Bedtime", "Praise", "Gratitude"],
  "5-7": ALL_CATS,
  "8-10": ALL_CATS,
};

const Kids = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [songs, setSongs] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (user === false) navigate("/signin");
  }, [user, navigate]);

  useEffect(() => {
    fetchSongs().then(setSongs).catch(() => {});
    return () => {
      if (audioRef.current) audioRef.current.pause();
    };
  }, []);

  if (!user) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#FFFDF6]">
        <Loader2 className="h-8 w-8 animate-spin text-[#FFC107]" />
      </div>
    );
  }

  const children = user.children || [];
  const selected = children.find((c) => c.child_id === params.get("child")) || children[0];

  const toggle = (song) => {
    if (playingId === song.song_id) {
      audioRef.current?.pause();
      audioRef.current = null;
      setPlayingId(null);
      return;
    }
    audioRef.current?.pause();
    setPlayingId(song.song_id);
    if (song.audio_url) {
      const a = new Audio(resolveImage(song.audio_url));
      audioRef.current = a;
      a.onended = () => setPlayingId(null);
      a.play().catch(() => {});
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF6] pb-24 pt-16" data-testid="kids-page">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <Link to="/profile" className="text-sm font-bold text-slate-400 transition-colors hover:text-slate-700" data-testid="kids-back-link">
          &larr; Grown-ups' area
        </Link>

        {children.length === 0 ? (
          <div className="mt-16 text-center" data-testid="kids-empty">
            <Star className="mx-auto h-12 w-12 fill-[#FFC107] text-[#FFC107]" />
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">No child profiles yet</h1>
            <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">
              Add a child profile from your account page — they're always private and only you
              can see them.
            </p>
            <Link
              to="/profile"
              data-testid="kids-add-child-link"
              className="mt-6 inline-block rounded-full bg-slate-900 px-8 py-3 font-display text-base font-semibold text-white"
            >
              Add a child profile
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="kids-child-picker">
              {children.map((c) => (
                <button
                  key={c.child_id}
                  onClick={() => setParams({ child: c.child_id })}
                  data-testid={`kids-child-${c.child_id}`}
                  className={`flex items-center gap-2 rounded-full border-2 px-5 py-2.5 font-display text-base font-semibold transition-all ${
                    selected?.child_id === c.child_id
                      ? "border-transparent text-white shadow-lg scale-105"
                      : "border-[#E2DDD0] bg-white text-slate-600"
                  }`}
                  style={selected?.child_id === c.child_id ? { backgroundColor: c.color } : {}}
                >
                  {c.name}
                  <span className="flex items-center gap-1 text-xs font-bold opacity-80">
                    <Lock className="h-3 w-3" /> private
                  </span>
                </button>
              ))}
            </div>

            {selected && (
              <motion.div
                key={selected.child_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-10"
              >
                <h1 className="font-display text-3xl font-bold text-slate-900 sm:text-4xl" data-testid="kids-greeting">
                  Hi, {selected.name}!
                </h1>
                <p className="mt-2 font-hand text-2xl text-slate-500">
                  pick a song and let's sing together
                </p>
                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  {songs
                    .filter((s) => AGE_CATS[selected.age_band]?.includes(s.category))
                    .map((song) => (
                      <motion.button
                        key={song.song_id}
                        onClick={() => toggle(song)}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        data-testid={`kids-song-${song.song_id}`}
                        className="flex items-center gap-4 rounded-3xl border border-[#E2DDD0] bg-white p-5 text-left shadow-[0_10px_30px_-5px_rgba(234,179,8,0.15)]"
                      >
                        <span
                          className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl text-white"
                          style={{ backgroundColor: song.color }}
                        >
                          {playingId === song.song_id ? (
                            <Pause className="h-8 w-8 fill-white" />
                          ) : (
                            <Play className="ml-1 h-8 w-8 fill-white" />
                          )}
                        </span>
                        <span>
                          <span className="block font-display text-lg font-bold text-slate-900">{song.title}</span>
                          <span className="mt-0.5 flex items-center gap-1 text-sm font-semibold text-slate-400">
                            {song.category === "Bedtime" && <Moon className="h-3.5 w-3.5" />}
                            {song.category}
                          </span>
                        </span>
                      </motion.button>
                    ))}
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Kids;
